// bloom — subtle always-on halation / glow around bright highlights.
//
// Real Polaroid (and most colour film) shows "halation": bright highlights
// bleed into adjacent darker pixels because light scatters in the emulsion
// before hitting the dye layers. This is what gives night/bar Polaroids that
// characteristic soft warm glow around lamps, candles, bottles, flash hits.
// Phones don't do this — they have anti-halation backing. So adding a fake
// bloom is the single biggest "feels like film" win available per pixel.
//
// Pipeline:
//   1. Extract bright pixels (luminance > 0.85 of 255 → 217).
//   2. Box-blur the bright-only pass with a small kernel (5×5 separable).
//   3. Composite back with `screen` blend at low alpha (~0.18) so it lifts
//      highlights without crushing midtones.
//
// Always fires (no probabilistic gate) — that's the point. The probabilistic
// step is lightLeak; bloom is the continuous baseline glow.
//
// Performance: 5×5 separable box blur over 1280×960 = ~1.2M reads + 1.2M
// writes per pass × 2 passes = ~5M ops, ~15ms on a 2020 iPhone. Fits the
// existing <800ms total budget comfortably.

import {
  type FilterContext,
  clamp,
  getImageData,
  luma,
  putImageData,
  screenBlend,
} from './_helpers'

// Luminance threshold (0..255) above which a pixel contributes to the bloom.
// 217 = 0.85 × 255. Below this, bloom pass writes 0; above, it writes the
// pixel's chroma directly so coloured highlights bloom in their own colour.
const LUMA_THRESHOLD = 217
// Mix strength when compositing the blurred bright pass back over the source.
// Higher = more visible glow. 0.18 reads as "soft halation" without smearing
// the whole image.
const BLOOM_MIX = 0.18
// Separable box-blur radius. 2 → 5×5 kernel. Larger = softer glow but slower.
const BLUR_RADIUS = 2

export function bloom(c: FilterContext): void {
  const img = getImageData(c.ctx)
  const { width, height } = c
  const src = img.data

  // 1. Extract bright pass. Allocate a single Float32 RGB buffer (no alpha —
  // halation is opaque additive). We use the source pixel's chroma so a red
  // lamp blooms red, not white.
  const len = width * height
  const bright = new Float32Array(len * 3)
  for (let i = 0, j = 0; i < src.length; i += 4, j += 3) {
    const r = src[i] ?? 0
    const g = src[i + 1] ?? 0
    const b = src[i + 2] ?? 0
    const l = luma(r, g, b)
    if (l < LUMA_THRESHOLD) continue
    // Weight by how far above threshold we are — smooths the bloom onset so
    // there's no harsh edge at the threshold line.
    const w = (l - LUMA_THRESHOLD) / (255 - LUMA_THRESHOLD)
    bright[j] = r * w
    bright[j + 1] = g * w
    bright[j + 2] = b * w
  }

  // 2. Separable box blur (horizontal then vertical). Faster than a single 2D
  // pass, gives same result for box kernel.
  const tmp = new Float32Array(len * 3)
  boxBlurH(bright, tmp, width, height, BLUR_RADIUS)
  boxBlurV(tmp, bright, width, height, BLUR_RADIUS)

  // 3. Composite blurred bright pass over source with `screen` blend.
  // Out = src + (blurredBright - src) * BLOOM_MIX (mix), then screen-blended.
  for (let i = 0, j = 0; i < src.length; i += 4, j += 3) {
    const baseR = src[i] ?? 0
    const baseG = src[i + 1] ?? 0
    const baseB = src[i + 2] ?? 0
    const bR = bright[j] ?? 0
    const bG = bright[j + 1] ?? 0
    const bB = bright[j + 2] ?? 0
    const sR = screenBlend(baseR, bR)
    const sG = screenBlend(baseG, bG)
    const sB = screenBlend(baseB, bB)
    src[i] = clamp(baseR + (sR - baseR) * BLOOM_MIX)
    src[i + 1] = clamp(baseG + (sG - baseG) * BLOOM_MIX)
    src[i + 2] = clamp(baseB + (sB - baseB) * BLOOM_MIX)
    // Alpha untouched.
  }

  putImageData(c.ctx, img)
}

function boxBlurH(
  src: Float32Array,
  dst: Float32Array,
  width: number,
  height: number,
  radius: number,
): void {
  const divisor = radius * 2 + 1
  for (let y = 0; y < height; y += 1) {
    const rowStart = y * width * 3
    for (let x = 0; x < width; x += 1) {
      let r = 0
      let g = 0
      let b = 0
      for (let k = -radius; k <= radius; k += 1) {
        const sx = clampInt(x + k, 0, width - 1)
        const j = rowStart + sx * 3
        r += src[j] ?? 0
        g += src[j + 1] ?? 0
        b += src[j + 2] ?? 0
      }
      const d = rowStart + x * 3
      dst[d] = r / divisor
      dst[d + 1] = g / divisor
      dst[d + 2] = b / divisor
    }
  }
}

function boxBlurV(
  src: Float32Array,
  dst: Float32Array,
  width: number,
  height: number,
  radius: number,
): void {
  const divisor = radius * 2 + 1
  for (let x = 0; x < width; x += 1) {
    for (let y = 0; y < height; y += 1) {
      let r = 0
      let g = 0
      let b = 0
      for (let k = -radius; k <= radius; k += 1) {
        const sy = clampInt(y + k, 0, height - 1)
        const j = (sy * width + x) * 3
        r += src[j] ?? 0
        g += src[j + 1] ?? 0
        b += src[j + 2] ?? 0
      }
      const d = (y * width + x) * 3
      dst[d] = r / divisor
      dst[d + 1] = g / divisor
      dst[d + 2] = b / divisor
    }
  }
}

function clampInt(n: number, lo: number, hi: number): number {
  if (n < lo) return lo
  if (n > hi) return hi
  return n
}
